import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Switch
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch } from "react-redux";
import { messageShow } from "../../common/api/actions";
import { updateEmailSettings } from "../api/action/mutation";
import { getEmailSettings } from "../api/action/query";
import { useHistory } from "react-router-dom";

const Settings = ({ initialSettings = {}, onSettingsUpdate }) => {
  const dispatch = useDispatch();
  const navigate = useHistory();

  const [sendgridApiKey, setSendgridApiKey] = useState("");
  const [batchSize, setBatchSize] = useState("");
  const [dailyLimit, setDailyLimit] = useState("");
  const [remainingLimit, setRemainingLimit] = useState("");
  const [email, setEmail] = useState("");
  const [isNewsletterOtpEnabled, setIsNewsletterOtpEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getEmailSettings();
        if (!data) throw new Error("Failed to load settings");
        setSendgridApiKey(data.sendgridApiKey || "");
        setBatchSize(data.batchSize || "");
        setDailyLimit(data.dailyLimit || "");
        setEmail(data.email || "");
        setRemainingLimit(data.remainingLimit || "");
        setIsNewsletterOtpEnabled(data.isNewsletterOtpEnabled || false);
        if (onSettingsUpdate) onSettingsUpdate(data);
      } catch (err) {
        dispatch(messageShow(err.message || "Failed to load settings"));
      }
    };
    fetchSettings();
  }, [dispatch, onSettingsUpdate]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const reqObj = {
        sendgridApiKey,
        batchSize: batchSize ? Number(batchSize) : 0,
        dailyLimit: dailyLimit ? Number(dailyLimit) : 0,
        email,
        isNewsletterOtpEnabled
      };
      const data = await updateEmailSettings(reqObj);
      if (data && data.error) throw new Error(data.error);
      setSendgridApiKey(data.sendgridApiKey || "");
      setBatchSize(data.batchSize || "");
      setDailyLimit(data.dailyLimit || "");
      setEmail(data.email || "");
      setRemainingLimit(data.remainingLimit || "");
      setIsNewsletterOtpEnabled(data.isNewsletterOtpEnabled || false);
      if (onSettingsUpdate) onSettingsUpdate(data);
      dispatch(messageShow("Settings saved successfully!"));
    } catch (err) {
      dispatch(messageShow(err.message || "Error saving settings"));
    }
    setIsSaving(false);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 550 }}>
        <IconButton onClick={() => navigate.goBack()} sx={{ mb: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        Email Settings
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="standard"
            label="SendGrid API Key"
            value={sendgridApiKey}
            onChange={(e) => setSendgridApiKey(e.target.value)}
            placeholder="Paste your SendGrid API key here"
            autoFocus
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            variant="standard"
            label="Sender Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. support@yourcompany.com"
            helperText="The email address from which your emails will be sent."
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            variant="standard"
            label="Batch Size"
            type="number"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            placeholder="E.g., 50"
            helperText="How many emails to send in one go."
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            variant="standard"
            label="Daily Email Limit"
            type="number"
            value={dailyLimit}
            onChange={(e) => setDailyLimit(e.target.value)}
            placeholder="E.g., 500"
            inputProps={{ min: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            variant="standard"
            label="Remaining Limit"
            value={remainingLimit}
            InputProps={{
              readOnly: true,
            }}
            disabled
            helperText="Emails remaining for today."
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            variant="standard"
            label="Remaining Limit"
            value={remainingLimit}
            InputProps={{
              readOnly: true,
            }}
            disabled
            helperText="Emails remaining for today."
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={isNewsletterOtpEnabled}
                onChange={(e) => setIsNewsletterOtpEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enable OTP verification for newsletter subscription"
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
              sx={{ backgroundColor: "#0D3559", color: "white" }}
            >
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
